import Badge from 'components/base/Badge';
import EcomProductQtySoldChart from 'components/charts/e-charts/EcomProductQtySoldChart';
import { Card, Form, Spinner } from 'react-bootstrap';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { PRODUCT_QTY_SOLD_QUERY } from 'api/graphql/queries/productQtySoldQuery';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBoxOpen } from '@fortawesome/free-solid-svg-icons';

interface EcomProductQtySoldCardProps {
  days?: number;
}

const EcomProductQtySoldCard: React.FC<EcomProductQtySoldCardProps> = ({ days = 7 }) => {
  const { t } = useTranslation();
  const [showAmount, setShowAmount] = useState<boolean>(false);
  // Fetch product qty sold data
  const { data, loading, error } = useQuery(PRODUCT_QTY_SOLD_QUERY, {
    variables: { days },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first'
  });

  // Extract data
  const dailyQty = data?.getProductQtySold?.dailyQty || [];
  const totalQty = data?.getProductQtySold?.totalQty || 0;
  const percentageChange = data?.getProductQtySold?.percentageChange || 0;
  
  // Format percentage for display
  const formattedPercentage = `${percentageChange >= 0 ? '+' : ''}${percentageChange.toFixed(1)}%`;
  const isPositiveChange = percentageChange >= 0;
  
  // Handle view type change (qty or amount)
  const handleViewTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setShowAmount(e.target.value === 'amount');
  };

  return (
    <Card 
      className="h-100 shadow-sm border-0 overflow-hidden"
      style={{
        background: 'var(--phoenix-card-bg, linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%))',
        borderRadius: '0.75rem'
      }}
    >
      <Card.Header className="p-3 d-flex flex-column justify-content-center">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="mb-0 fw-bold text-body">
            <FontAwesomeIcon icon={faBoxOpen} className="me-2" />
            {t('productQtySold.title')}
          </h5>
          <Form.Select 
            size="sm" 
            style={{ width: 'auto' }} 
            value={showAmount ? 'amount' : 'qty'}
            onChange={handleViewTypeChange}
          >
            <option value="qty">{t('quantity')}</option>
            <option value="amount">{t('transactionSummary.revenue')}</option>
          </Form.Select>
        </div>
      </Card.Header>
      <Card.Body className="pt-1 pb-3 px-3 d-flex flex-column justify-content-center">
        {loading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
            <Spinner animation="border" role="status" variant="primary">
              <span className="visually-hidden">{t('loading')}</span>
            </Spinner>
          </div>
        ) : (
          <>
            <div className="d-flex justify-content-between mb-2">
              <div>
                <h6 className="text-body-tertiary">{t('salesSummary.last30days')}</h6>
              </div>
              <div>
                <h4 className="mb-0">
                  {totalQty}
                  <Badge 
                    bg={isPositiveChange ? "success" : "danger"} 
                    variant="phoenix"
                    pill 
                    className="ms-2"
                    style={{ fontSize: '0.65rem', verticalAlign: 'middle' }}
                  >
                    {formattedPercentage}
                  </Badge>
                </h4>
              </div>
            </div>
            <div className="pb-0 pt-4">
              <EcomProductQtySoldChart 
                data={dailyQty} 
                showAmount={showAmount} 
              />
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default EcomProductQtySoldCard;
